import { createRouter, createWebHistory } from 'vue-router'
import { getAuthToken } from '../api/auth'
import http from '../api/http'

// 登录页面
import Login from '../components/pages/Login.vue'
import { useAppStore } from '@/stores'

// 纯内容组件
import ReportForms from '../components/pages/ReportForms.vue'
import DepartmentManagement from '../components/pages/DepartmentManagement.vue'
import TeacherStatus from '../components/pages/TeacherStatus.vue'
import MyAchievements from '../components/pages/MyAchievements.vue'
import PersonalInfo from '../components/pages/PersonalInfo.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'ReportForms',
    component: ReportForms,
    meta: { requiresAuth: true, moduleName: '填报信息' }
  },
  {
    path: '/department',
    name: 'DepartmentManagement',
    component: DepartmentManagement,
    meta: { requiresAuth: true, moduleName: '教研室管理' }
  },
  {
    path: '/teacher-status',
    name: 'TeacherStatus',
    component: TeacherStatus,
    meta: { requiresAuth: true, moduleName: '教师状态维护' }
  },
  {
    path: '/my-achievements',
    name: 'MyAchievements',
    component: MyAchievements,
    meta: { requiresAuth: true, moduleName: '我的成果' }
  },
  {
    path: '/personal-info',
    name: 'PersonalInfo',
    component: PersonalInfo,
    meta: { requiresAuth: true, moduleName: '个人信息' }
  },
  {
    path: '/info-overview',
    name: 'InfoOverview',
    component: () => import('../components/pages/InfoOverview.vue'),
    meta: { requiresAuth: true, moduleName: '信息总览' }
  },
  {
    path: '/info-overview/export',
    name: 'ExportData',
    component: () => import('../components/pages/ExportData.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/permission-settings',
    name: 'PermissionSettings',
    component: () => import('../components/pages/PermissionSettings.vue'),
    meta: { requiresAuth: true, adminOnly: true }
  },
  // 错误页面
  {
    path: '/403',
    name: 'Error403',
    component: () => import('../components/pages/error/Error403.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/404',
    name: 'Error404',
    component: () => import('../components/pages/error/Error404.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/500',
    name: 'Error500',
    component: () => import('../components/pages/error/Error500.vue'),
    meta: { requiresAuth: false }
  },
  // 兜底路由，匹配所有未定义路径
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 验证token是否有效（可通过环境变量关闭，避免后端未部署导致卡住）
const SHOULD_VALIDATE_TOKEN = process.env.VUE_APP_VALIDATE_TOKEN === 'true'
async function validateToken() {
  if (!SHOULD_VALIDATE_TOKEN) return true
  try {
    const res = await Promise.race([
      http.get('/user/info/my'),
      new Promise((resolve) => setTimeout(() => resolve({ status: 200, data: { code: 1 } }), 2000))
    ])
    return res?.data?.code === 1 || res?.status === 200
  } catch {
    return false
  }
}

// 路由守卫：检查登录状态
router.beforeEach(async (to, from, next) => {
  const token = getAuthToken()
  const requiresAuth = to.meta.requiresAuth !== false

  if (requiresAuth && !token) {
    // 需要登录但没有token，跳转到登录页
    next('/login')
  } else if (requiresAuth && token) {
    // 有token，验证token是否有效
    const isValid = await validateToken()
    if (isValid) {
      const store = useAppStore()
      await store.ensurePermissionsSynced(getAuthToken())
      if (to.meta?.adminOnly && !store.isAdmin()) {
        next('/403')
        return
      }
      if (to.meta?.moduleName && !store.hasModuleByName(to.meta.moduleName)) {
        next('/403')
        return
      }
      next()
    } else {
      // token无效，清除并跳转到登录页
      localStorage.removeItem('Authorization')
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      next('/login')
    }
  } else if (to.path === '/login' && token) {
    // 已登录用户访问登录页，验证token
    const isValid = await validateToken()
    if (isValid) {
      // token有效，跳转到首页
      next('/')
    } else {
      // token无效，清除并允许访问登录页
      localStorage.removeItem('Authorization')
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      next()
    }
  } else {
    // 不需要认证的页面，直接访问
    next()
  }
})

export default router
